// Support — Level 1 AI help. A chat screen that calls the guarded
// /api/miniapp/support/message endpoint. Conversation lives in component
// state (client-side history, v1); the whole thread is sent on each turn
// because the backend is stateless.
//
// Navigation:
//   Back → / (card dashboard)

import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { sendSupportMessage, type SupportTurn } from '../api/client.ts';
import { IconArrowUp, IconChevronLeft } from '../components/icons.tsx';

import { rootRoute } from './__root.tsx';

const GREETING = 'Hi — I can help with questions about your Leadtone card and the Vault. Ask away.';

// ─── Message bubble ────────────────────────────────────────────────────────

// Turn URLs, emails, and @handles in assistant text into tappable links.
// Assistant text is model output (not raw user input), and we build every
// href ourselves from a matched token — so no HTML is injected.
const linkify = (raw: string): ReactNode[] => {
  // The agent emits markdown, but bubbles render plain text — strip bold/italic
  // markers so ** and * don't show literally (and don't leak into hrefs).
  const text = raw
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(?<!\*)\*(?!\*)([^*]+)\*(?!\*)/g, '$1');

  const pattern = /(https?:\/\/[^\s]+)|([\w.+-]+@[\w-]+\.[\w.-]+)|(@[A-Za-z]\w{3,})/g;
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const style = { color: 'var(--accent, #6ea3de)', textDecoration: 'underline' };
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const [tok, url, email, handle] = m;
    const href = url ?? (email ? `mailto:${email}` : `https://t.me/${(handle ?? '').slice(1)}`);
    out.push(
      <a key={m.index} href={href} target="_blank" rel="noopener noreferrer" style={style}>
        {tok}
      </a>,
    );
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};
const Bubble = ({ role, children }: { readonly role: SupportTurn['role']; readonly children: ReactNode }): ReactNode => {
  const isUser = role === 'user';
  return (
    <div
      style={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '82%',
        background: isUser ? 'var(--navy-deep)' : 'var(--bg-elevated)',
        border: isUser ? 'none' : '1px solid rgba(232, 230, 223, 0.08)',
        color: 'var(--text-primary)',
        borderRadius: 10,
        padding: '9px 13px',
        fontSize: 14.5,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {role === 'assistant' && typeof children === 'string' ? linkify(children) : children}
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────

const SupportPage = (): ReactNode => {
  const navigate = useNavigate();
  const [turns, setTurns] = useState<readonly SupportTurn[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, pending, error]);

  const onSend = (): void => {
    const text = input.trim();
    if (text === '' || pending) return;
    const next: readonly SupportTurn[] = [...turns, { role: 'user', content: text }];
    setTurns(next);
    setInput('');
    setError(null);
    setPending(true);
    void sendSupportMessage(next)
      .then(({ reply }): void => {
        setTurns([...next, { role: 'assistant', content: reply }]);
      })
      .catch((): void => {
        setError('Something went wrong. Try again, or contact @Leadtone_Support.');
      })
      .finally((): void => {
        setPending(false);
      });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <main className="screen">
      <div className="topbar fade-in">
        <button
          type="button"
          className="topbar-back"
          aria-label="Back"
          onClick={(): void => {
            void navigate({ to: '/' });
          }}
        >
          <IconChevronLeft />
        </button>
        <p className="eyebrow">Support</p>
        <span className="topbar-spacer" aria-hidden="true" />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          paddingBottom: 96,
        }}
        className="fade-in"
      >
        <Bubble role="assistant">{GREETING}</Bubble>

        {turns.map((m, i) => (
          <Bubble key={i} role={m.role}>
            {m.content}
          </Bubble>
        ))}

        {pending ? (
          <div
            style={{
              alignSelf: 'flex-start',
              color: 'var(--text-tertiary)',
              fontSize: 13,
              padding: '4px 4px',
            }}
          >
            Thinking…
          </div>
        ) : null}

        {error !== null ? (
          <div
            role="alert"
            style={{
              alignSelf: 'flex-start',
              color: 'var(--danger)',
              fontSize: 13.5,
              lineHeight: 1.5,
              padding: '2px 4px',
            }}
          >
            {error}
          </div>
        ) : null}

        <div ref={endRef} />
      </div>

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--bg-canvas)',
          borderTop: '1px solid rgba(232, 230, 223, 0.09)',
          paddingTop: 12,
          paddingBottom: 14,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 10,
        }}
      >
        <textarea
          value={input}
          onChange={(e): void => {
            setInput(e.target.value);
          }}
          onKeyDown={onKeyDown}
          placeholder="Ask a question"
          rows={1}
          aria-label="Ask a question"
          style={{
            flex: 1,
            resize: 'none',
            maxHeight: 120,
            background: 'var(--bg-elevated)',
            border: '1px solid rgba(232, 230, 223, 0.1)',
            borderRadius: 10,
            color: 'var(--text-primary)',
            fontSize: 14.5,
            lineHeight: 1.4,
            padding: '10px 12px',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
        <button
          type="button"
          aria-label="Send"
          onClick={onSend}
          style={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: 10,
            border: 'none',
            background: 'var(--sapphire)',
            color: 'var(--text-display)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            opacity: input.trim() === '' || pending ? 0.5 : 1,
          }}
        >
          <IconArrowUp size={18} />
        </button>
      </div>
    </main>
  );
};

export const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/support',
  component: SupportPage,
});
